import type { ReactNode } from "react";
import {
  Menu,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
  OptionPanelNavRow,
  OptionPanelRow,
  Switch,
} from "@salestrak/ui";

import type {
  CardDisplayOptions,
  FieldLabelMode,
  TextLabelLayout,
} from "#app/components/issue-card";
import type { BoardCardSize } from "#app/lib/view-config";

// ---------------------------------------------------------------------------
// CardLayoutRows — card-wide layout modes
// ---------------------------------------------------------------------------
//
// A board contributes these rows through ViewOptionsPanel's `display` slot.
// The map uses them in its controlled card-options panel. They shape how each
// issue card lays its fields out, so they sit beside the other display toggles
// rather than on the Fields page, which owns the per-item roster.
//
// The host decides whether its settings are editable.
// ---------------------------------------------------------------------------

/**
 * One mode row: the active mode reads in place as the row's quiet value and
 * the whole row opens the choice list, the way the panel's Group by and Sort
 * by rows report and edit theirs. A menu, not a segmented control on the
 * row's edge: the row is one of a list of `label … value ›` rows, and a
 * two-button toggle in the value column is a second control grammar in it.
 */
export function ModeRow<T extends string>({
  label,
  value,
  options,
  disabled = false,
  onChange,
}: {
  label: string;
  value: T;
  /** `meta` is a quiet fact beside the option in the list ("Default"). */
  options: { value: T; label: string; meta?: string }[];
  disabled?: boolean;
  onChange: (value: T) => void;
}) {
  const active = options.find((opt) => opt.value === value);
  return (
    <Menu>
      <MenuTrigger
        disabled={disabled}
        render={
          <OptionPanelNavRow
            label={label}
            value={active?.label}
            disabled={disabled}
          />
        }
      />
      {/* The list opens under the value it edits. A mode is a choice, not a
          toggle: picking one closes the popup the way a select would. */}
      <MenuPopup align="end" width="select">
        <MenuRadioGroup
          value={value}
          onValueChange={(next) => {
            if (typeof next === "string" && next !== value) onChange(next as T);
          }}
        >
          {options.map((opt) => (
            <MenuRadioItem
              key={opt.value}
              value={opt.value}
              meta={opt.meta}
              closeOnClick
            >
              {opt.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  );
}

export type CardSizeRowProps = {
  value: BoardCardSize;
  onChange: (value: BoardCardSize) => void;
  disabled?: boolean;
};

const CARD_SIZE_OPTIONS: { value: BoardCardSize; label: string; meta?: string }[] =
  [
    { value: "medium", label: "Medium" },
    { value: "large", label: "Large", meta: "Default" },
  ];

/**
 * The board's "Card size" row — the column width rung, since every card is
 * its column's width. A BOARD setting, not a card option: the map's cards
 * have no column, so it sits beside `CardLayoutRows` in the board's display
 * slot rather than inside it. Same mode-row grammar as those, so the display
 * group reads as one list; the rung set is open-ended (a Small is expected)
 * and the list grows a row.
 */
export function CardSizeRow({
  value,
  onChange,
  disabled = false,
}: CardSizeRowProps) {
  return (
    <ModeRow<BoardCardSize>
      label="Card size"
      value={value}
      options={CARD_SIZE_OPTIONS}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

export type CardLayoutRowsProps = {
  options: CardDisplayOptions;
  /** Patch-merge into the view's draft card options. */
  onChange: (patch: Partial<CardDisplayOptions>) => void;
  disabled?: boolean;
  /** Host switch rows seated with "Show empty fields", under the mode rows:
   *  the group's value rows stand together, then its switches. */
  switches?: ReactNode;
};

export function CardLayoutRows({
  options,
  onChange,
  disabled = false,
  switches,
}: CardLayoutRowsProps) {
  return (
    <>
      <ModeRow<FieldLabelMode>
        label="Field labels"
        value={options.fieldLabelMode}
        options={[
          { value: "text", label: "Text" },
          { value: "icon", label: "Icons" },
        ]}
        disabled={disabled}
        onChange={(fieldLabelMode) => onChange({ fieldLabelMode })}
      />
      {/* Text labels' one sub-setting, shown only while Text is picked:
          beside the value on the view's shared rail (Horizontal, the
          default) or on its own line above it (Vertical). Icons have no
          layout to choose. */}
      {options.fieldLabelMode === "text" ? (
        <ModeRow<TextLabelLayout>
          label="Label layout"
          value={options.textLabelLayout}
          options={[
            { value: "horizontal", label: "Horizontal", meta: "Default" },
            { value: "vertical", label: "Vertical" },
          ]}
          disabled={disabled}
          onChange={(textLabelLayout) => onChange({ textLabelLayout })}
        />
      ) : null}
      {/* Text labels have no width row: the rail is sized per view to the
          widest shown name (capped at half the card), so values align down
          the whole board without a setting to get wrong. */}
      {/* `fieldLayout` has no row: the "Stack fields" switch (on = one
          full-width token per line, `list`; off = a `wrap` mosaic) is held
          back until the wrap layout's UX is settled, and every intake pins
          the value to `list` meanwhile (`parseViewConfig`, the map route). */}
      {switches}
      {/* Empty CUSTOM FIELDS only — empty properties already have a home
          (the footer's icon actions) and never render as rows. */}
      <OptionPanelRow label="Show empty fields" disabled={disabled}>
        <Switch
          size="sm"
          checked={options.showEmptyFields}
          disabled={disabled}
          onCheckedChange={(showEmptyFields) =>
            onChange({ showEmptyFields: !!showEmptyFields })
          }
        />
      </OptionPanelRow>
    </>
  );
}
